import Router, { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { CanvasQueryParams } from '~/constants/canvasQueryParams';
import { CANVAS_EXCLUDED_ROUTES, Routes } from '~/constants/routes';
import { useDiscardDraftCanvas } from '~/hooks/canvases/useDiscardDraftCanvas';
import { getBoardIdFromPath } from '~/utils/PathUtils';
import { useRouterQueryState } from '~/utils/useRouterQueryState';

const CANVAS_ID_PARAM_NAMES = [CanvasQueryParams.canvasId, CanvasQueryParams.canvasFullscreen];

type UseSelectedCanvasIdSyncOptions = {
  enabled?: boolean;
};

/**
 * Synchronizes selected canvas state between Zustand and URL query params.
 * Mount this hook once at a stable app-level location.
 */
export const useSelectedCanvasIdSync = ({ enabled = true }: UseSelectedCanvasIdSyncOptions = {}) => {
  const router = useRouter();
  const isRouterReady = router.isReady;
  const discardDraftCanvas = useDiscardDraftCanvas();
  const {
    setValues,
    values: { cnId, cnFs },
  } = useRouterQueryState(CANVAS_ID_PARAM_NAMES);

  const open = useWorkspaceRightPanelOpen();
  const isFullscreen = useWorkspaceRightPanelStore((state) => state.isFullscreen);
  const storedCanvasId = useWorkspaceRightPanelStore((state) => state.selectedCanvasId);
  const setSelectedCanvasId = useWorkspaceRightPanelStore((state) => state.setSelectedCanvasId);
  const setOpen = useWorkspaceRightPanelStore((state) => state.setOpen);
  const setFullscreen = useWorkspaceRightPanelStore((state) => state.setFullscreen);

  const prevPathnameRef = useRef(router.pathname);
  const prevPathnameForSyncRef = useRef(router.pathname);
  const prevCnIdRef = useRef<string | null>(null);
  const prevCnFsRef = useRef<string | null>(null);
  const prevOpenRef = useRef(open);
  const closingOnExcludedRef = useRef(false);
  const prevBoardIdRef = useRef<string | undefined>(getBoardIdFromPath(router.asPath));
  const hasRunHydrationClearRef = useRef(false);
  // True while a popstate (browser back/forward) is in-flight. Reset via rAF after
  // effects run so Effect 2 and Effect 5 can read it in the same render cycle.
  const isPopStateRef = useRef(false);

  const isAssetModalRoute = router.pathname.includes('/c/[clipId]');

  // Track browser back/forward so Effect 2 only closes the canvas on popstate, not on link
  // navigation. rAF reset ensures effects read the flag before it clears — React passive
  // effects (MessageChannel macrotasks) run before rAF paint callbacks.
  useEffect(() => {
    Router.beforePopState(() => {
      isPopStateRef.current = true;
      return true;
    });

    const handleRouteChangeComplete = () => {
      requestAnimationFrame(() => {
        isPopStateRef.current = false;
      });
    };

    Router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      Router.beforePopState(() => true);
      Router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, []);

  // URL is the source of truth for selected canvas on initial app load.
  // Once persisted right-panel state rehydrates, drop stale selectedCanvasId
  // when the URL does not explicitly request one.
  // Runs at most once: the ref prevents re-firing on subsequent pathname changes
  // (e.g. exiting the asset modal) where the URL restore effect hasn't yet run.
  useEffect(() => {
    if (!enabled || !isRouterReady || isAssetModalRoute || CANVAS_EXCLUDED_ROUTES.has(router.pathname)) {
      return;
    }

    if (hasRunHydrationClearRef.current) {
      return;
    }

    const clearStalePersistedCanvasIfNeeded = () => {
      hasRunHydrationClearRef.current = true;
      const params = new URLSearchParams(window.location.search);
      const urlCanvasId = params.get(CanvasQueryParams.canvasId);
      const selectedCanvasId = useWorkspaceRightPanelStore.getState().selectedCanvasId;

      if (!urlCanvasId && selectedCanvasId) {
        setSelectedCanvasId(null);
        setOpen(false);
      }
    };

    if (useWorkspaceRightPanelStore.persist.hasHydrated()) {
      clearStalePersistedCanvasIfNeeded();
      return;
    }

    const unsubscribe = useWorkspaceRightPanelStore.persist.onFinishHydration(() => {
      clearStalePersistedCanvasIfNeeded();
    });

    return unsubscribe;
  }, [enabled, isRouterReady, isAssetModalRoute, router.pathname, setSelectedCanvasId, setOpen]);

  // Hydrate selected canvas from URL on URL-driven changes (deep links/back-forward).
  useEffect(() => {
    if (!enabled || !isRouterReady) {
      return;
    }

    if (isAssetModalRoute) {
      prevCnIdRef.current = cnId || null;
      return;
    }

    const currentCnId = cnId || null;
    const didCnIdChange = prevCnIdRef.current !== currentCnId;

    // Check excluded routes before the didCnIdChange guard so we catch both:
    // (1) cnId just arrived on an excluded route (didCnIdChange = true), and
    // (2) cnId was already tracked (e.g. carried from asset modal) but pathname just changed
    //     to an excluded route (didCnIdChange = false).
    // The excluded-route effect below owns the redirect; just track cnId here so the
    // post-redirect run sees a changed value and opens the canvas.
    if (CANVAS_EXCLUDED_ROUTES.has(router.pathname)) {
      prevCnIdRef.current = cnId || null;
      return;
    }

    if (didCnIdChange) {
      if (cnId) {
        const currentStoredCanvasId = useWorkspaceRightPanelStore.getState().selectedCanvasId;

        if (cnId !== currentStoredCanvasId) {
          setSelectedCanvasId(cnId);
        }
        setOpen(true);
      } else if (isPopStateRef.current) {
        // cnId removed by browser back/forward — URL is authoritative, close the canvas.
        // Clear selectedCanvasId immediately so Effect 5 reads null from getState() in the
        // same render and does not re-push the old cnId.
        setSelectedCanvasId(null);
        // Suppress close when a draft is in-flight: cnId disappearance may be from store→URL
        // sync clearing a stale rehydrated cnId, not the user navigating away.
        const hasDraftInFlight = !!useWorkspaceRightPanelStore.getState().draftCanvas;

        if (!hasDraftInFlight) {
          setOpen(false);
        }
      }
      // else: pushstate navigation (link, router.push) — leave canvas open.
      // Effect 5 will restore cnId into the URL.
    }

    prevCnIdRef.current = currentCnId;
  }, [enabled, isRouterReady, isAssetModalRoute, cnId, router.pathname, setSelectedCanvasId, setOpen]);

  // Hydrate fullscreen state from URL on URL-driven changes.
  useEffect(() => {
    if (!enabled || !isRouterReady) {
      return;
    }

    const currentCnFs = cnFs || null;
    const didCnFsChange = prevCnFsRef.current !== currentCnFs;

    if (didCnFsChange) {
      setFullscreen(cnFs === 'true');
    }

    prevCnFsRef.current = currentCnFs;
  }, [enabled, isRouterReady, cnFs, setFullscreen]);

  // Remove the pre-hydration CSS override once React has taken over rendering.
  // The attribute was set by an inline script in _document.tsx to prevent layout flash.
  useEffect(() => {
    if (!enabled || !isRouterReady) {
      return;
    }
    document.documentElement.removeAttribute('data-canvas-fullscreen');
  }, [enabled, isRouterReady]);

  // Keep URL in sync with store (single owner).
  // biome-ignore lint/correctness/useExhaustiveDependencies(isFullscreen): kept as a trigger so this effect re-runs when fullscreen changes, even though we read via getState() to avoid stale closures
  // biome-ignore lint/correctness/useExhaustiveDependencies(storedCanvasId): kept as a trigger so this effect re-runs when selectedCanvasId changes, even though we read via getState() to avoid stale closures
  useEffect(() => {
    if (!enabled || !isRouterReady) {
      return;
    }

    if (isAssetModalRoute) {
      return;
    }

    if (CANVAS_EXCLUDED_ROUTES.has(router.pathname)) {
      return;
    }

    // Read selectedCanvasId directly from store to avoid stale closure, matching
    // the isFullscreen pattern below. The URL→store effect (above) may have already
    // called setSelectedCanvasId() in the same render cycle.
    const latestStoredCanvasId = useWorkspaceRightPanelStore.getState().selectedCanvasId;
    const nextCnId = latestStoredCanvasId ?? null;
    // Read fullscreen directly from store to avoid stale closure on initial hydration.
    // Effect 2 (URL→store) may have already called setFullscreen() in the same render cycle,
    // but the component closure still holds the previous isFullscreen value.
    const currentIsFullscreen = useWorkspaceRightPanelStore.getState().isFullscreen;
    const nextCnFs = latestStoredCanvasId && currentIsFullscreen ? 'true' : null;
    // Read URL params directly from window.location so this effect does not re-trigger on
    // URL-driven changes (browser back/forward). Those changes are owned by the URL→store effect.
    const params = new URLSearchParams(window.location.search);
    const currentCnId = params.get(CanvasQueryParams.canvasId) ?? null;
    const currentCnFs = params.get(CanvasQueryParams.canvasFullscreen) ?? null;
    const prevPathname = prevPathnameForSyncRef.current;
    prevPathnameForSyncRef.current = router.pathname;

    if (currentCnId !== nextCnId || currentCnFs !== nextCnFs) {
      const isPathnameChange = router.pathname !== prevPathname;

      // On popstate (back/forward), the URL is already correct — don't fight it.
      // On pushstate (link, router.push), restore cnId so canvas persists across navigation.
      if (isPathnameChange && !currentCnId && isPopStateRef.current) {
        return;
      }

      if (nextCnId && nextCnId !== currentCnId) {
        // Push when opening or switching canvases so browser back/forward navigates in/out.
        const nextQuery: Record<string, string | string[] | undefined> = { ...Router.query };
        nextQuery[CanvasQueryParams.canvasId] = nextCnId;
        if (nextCnFs) {
          nextQuery[CanvasQueryParams.canvasFullscreen] = nextCnFs;
        } else {
          delete nextQuery[CanvasQueryParams.canvasFullscreen];
        }
        Router.push({ pathname: Router.pathname, query: nextQuery }, undefined, { shallow: true });
      } else {
        // Replace (not push) for close and fullscreen-only changes — these don't need
        // their own history entries.
        setValues({ cnId: nextCnId, cnFs: nextCnFs });
      }
    }
  }, [enabled, isRouterReady, isAssetModalRoute, router.pathname, storedCanvasId, isFullscreen, setValues]);

  // Ensure close actions from anywhere clear selected canvas in store.
  // URL clearing is handled by the store->URL sync effect above.
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (!enabled || !isRouterReady) {
      return;
    }

    if (wasOpen && !open) {
      setSelectedCanvasId(null);
      discardDraftCanvas();
    }
  }, [enabled, isRouterReady, open, setSelectedCanvasId, discardDraftCanvas]);

  // Close or redirect when on a page that doesn't support the split-panel canvas layout.
  useEffect(() => {
    if (!enabled || !isRouterReady) {
      return;
    }

    if (!CANVAS_EXCLUDED_ROUTES.has(router.pathname)) {
      closingOnExcludedRef.current = false;
      return;
    }

    if (storedCanvasId) {
      closingOnExcludedRef.current = true;
      setOpen(false);
      setSelectedCanvasId(null);
      // If a deep-link cnId for a different canvas is present, redirect to it
      // rather than clearing — the stored canvas is already closed above.
      if (cnId && cnId !== storedCanvasId) {
        router.replace({ pathname: Routes.canvases, query: { [CanvasQueryParams.canvasId]: cnId } });
        prevCnIdRef.current = null;
      } else {
        setValues({ cnId: null, cnFs: null });
      }
    } else if (!cnId) {
      closingOnExcludedRef.current = false;
    } else if (!closingOnExcludedRef.current) {
      router.replace({ pathname: Routes.canvases, query: { [CanvasQueryParams.canvasId]: cnId } });
      prevCnIdRef.current = null;
    }
  }, [
    enabled,
    isRouterReady,
    router.pathname,
    router.replace,
    cnId,
    storedCanvasId,
    setOpen,
    setSelectedCanvasId,
    setValues,
  ]);

  // Strip cnId/cnFs when entering asset modal route, restore when leaving.
  useEffect(() => {
    if (!enabled || !isRouterReady) {
      return;
    }

    const currentPathname = router.pathname;
    const currentBoardId = getBoardIdFromPath(router.asPath);
    const prevPathname = prevPathnameRef.current;
    const prevBoardId = prevBoardIdRef.current;
    const isCurrentAssetModalRoute = currentPathname.includes('/c/[clipId]');
    const wasAssetModalRoute = prevPathname.includes('/c/[clipId]');

    if (isCurrentAssetModalRoute && !wasAssetModalRoute && cnId) {
      setValues({ cnId: null, cnFs: null });
    } else if (
      !isCurrentAssetModalRoute &&
      wasAssetModalRoute &&
      storedCanvasId &&
      !cnId &&
      currentBoardId === prevBoardId &&
      !CANVAS_EXCLUDED_ROUTES.has(currentPathname)
    ) {
      const currentIsFullscreen = useWorkspaceRightPanelStore.getState().isFullscreen;
      setValues({ cnId: storedCanvasId, cnFs: currentIsFullscreen ? 'true' : null });
    }

    prevPathnameRef.current = currentPathname;
    prevBoardIdRef.current = currentBoardId;
  }, [enabled, isRouterReady, router.pathname, router.asPath, cnId, storedCanvasId, setValues]);
};
